import { FC, PropsWithChildren, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { usePageContext } from "@ryde/redux";
import {
  fieldNameMetadataResolve,
  getGridFieldTypeNameById,
  getIsGridCellShownByCondition,
  GRID_DATETIME_FIELD_KEYS,
  ICustomField,
  NO_VALUE_DISPLAY,
  normalizeGridValue,
  profileDtoAtom,
  profilePermissionsSelector,
  SimpleResult,
  translateGridValue,
} from "@ryde/shared";

import { TextCellTemplate } from "../text-cell.template";
import { mapEntityToEditFn } from "./map-entity-to-edit-fn";
import { tableTemplateControlsMap } from "./map-table-template-controls";

export interface IRenderColumnTemplate extends ICustomField, PropsWithChildren {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  valueTranslated?: any;
  truncateChars?: number;
  editFn?: (id: number, data: unknown) => Promise<SimpleResult<unknown>>;
  rowId?: number;
  rowOriginal?: unknown; // original underlying row dto
  dateFormat?: string;
  timeFormat?: string;
  /** Custom field name before grid display alias resolution (e.g. driverId). */
  fieldName?: string;
}

export const RenderCellTemplate: FC<IRenderColumnTemplate> = props => {
  const {
    gridFieldTypeId,
    formAttributes,
    gridAttributes,
    name: fieldNameRaw,
    value: valueRaw,
    id,
    children,
    isColumn,
    gridVisibleCondition,
    rowOriginal,
  } = props;

  const { entity } = usePageContext();
  const profile = useRecoilValue(profileDtoAtom);
  const roleDataItem = useRecoilValue(profilePermissionsSelector);
  const { t } = useTranslation();

  const isCellShownByCondition = getIsGridCellShownByCondition({
    gridVisibleCondition,
    rowValues: rowOriginal as Record<string, unknown>,
    permissions: roleDataItem?.permissions,
  });

  const fieldNameResolved = fieldNameMetadataResolve(fieldNameRaw, isColumn);

  const controlInfoName = gridFieldTypeId
    ? getGridFieldTypeNameById(gridFieldTypeId)
    : undefined;

  const propsNormalized: IRenderColumnTemplate = useMemo(() => {
    const value = normalizeGridValue({
      valueToProcess: valueRaw,
      fieldNameResolved: fieldNameRaw,
      entity,
    });

    const valueTranslated = translateGridValue({
      valueToProcess: valueRaw,
      formAttributes,
      gridAttributes,
      skipBooleanSelectListLabelConversion: true,
      t,
    });

    return {
      ...props,
      ...(controlInfoName &&
      // to avoid unnecessary props providing
      GRID_DATETIME_FIELD_KEYS.includes(controlInfoName)
        ? {
            dateFormat: profile?.metadata?.dateFormat,
            timeFormat: profile?.metadata?.timeFormat,
          }
        : {}),
      fieldName: fieldNameRaw,
      name: fieldNameResolved,
      value,
      valueTranslated,
    };
  }, [
    controlInfoName,
    entity,
    fieldNameRaw,
    fieldNameResolved,
    formAttributes,
    gridAttributes,
    profile?.metadata?.dateFormat,
    profile?.metadata?.timeFormat,
    props,
    t,
    valueRaw,
  ]);

  const mapId = `${fieldNameRaw}_${id}`;

  if (!isCellShownByCondition) {
    return <>{NO_VALUE_DISPLAY}</>;
  }

  if (!controlInfoName) {
    return (
      <TextCellTemplate key={mapId} {...propsNormalized}></TextCellTemplate>
    );
  }

  const TemplateName = tableTemplateControlsMap[controlInfoName];

  if (!TemplateName) {
    return (
      <TextCellTemplate
        e2eData="unknown_cell_template"
        key={mapId}
        {...propsNormalized}
      />
    );
  }

  return (
    <TemplateName
      editFn={mapEntityToEditFn[entity]}
      e2eData={controlInfoName}
      key={mapId}
      {...propsNormalized}
    >
      {children}
    </TemplateName>
  );
};
